import SummaryTable from './SummaryTable';
import useTable from 'src/hooks/useTable';
import { useEffect, useState } from 'react';
import { PaginateQuery } from 'src/api/pagination/dtos';
import { workflowInstanceMap } from 'src/careflow/common/common-utils';
import { TABLE_HEAD_WORKFLOW_INSTANCE_PATIENT } from 'src/careflow/common/table-head';
import { Button, MenuItem } from '@mui/material';
import { Add } from '@mui/icons-material';
import { CaseForm } from './CaseForm';
import { PatientWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { usePatient } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { useSnackbar } from 'notistack';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { translateTableHead } from 'src/careflow/helpers/common';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { CaseService } from 'src/crs/case/service/CaseService';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { PrintableCarePlan } from '../../case/printable-care-plan/PrintableCarePlan';
import ArticleIcon from '@mui/icons-material/Article';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Edit from './Edit';

export default function PatientCase({ patientId, encounterId, disableActions, title, i18n }: any) {
  const [caseData, setCaseData] = useState<any>(null);
  const [refresh, setRefresh] = useState(false);
  const [add, setOpenAdd] = useState(false);
  const [edit, setOpenEdit] = useState(false);
  const [rowEdit, setRowEdit] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const authUser = useAuth().getCurrentUser();
  const { configurations, componentsData } = useTenantConfigData();

  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: 5,
    defaultOrderBy: 'id',
    initialIndex: 1,
    defaultCurrentPage: 1,
  });

  const [patient] = usePatient(patientId, { map: PatientWrapper });

  const getPaginateQuery = () => {
    if (!configurations || !patientId) return null;
    const paginationQuery: PaginateQuery = { page, limit: rowsPerPage };
    paginationQuery.filter = {
      indexedSubjectFhirRefUri: `${configurations.fhir.baseUrl}/Patient/${patientId}`,
      ...(encounterId && { indexedEncounterFhirRefId: encounterId }),
    };
    return paginationQuery;
  };

  const getCaseList = async () => {
    const query = getPaginateQuery();
    if (!query) return;
    const cases = await CaseService.getAll(query);
    if (cases) {
      setCaseData(cases);
    }
  };

  useEffect(() => {
    if (configurations && patientId) {
      getCaseList();
    }
  }, [page, rowsPerPage, patientId, configurations]);

  useEffect(() => {
    if (refresh && configurations && patientId) {
      getCaseList();
      setRefresh(false);
    }
  }, [refresh, configurations, patientId]);

  const handlePageChange = (event: unknown, newPage: number) => {
    onChangePage(event, newPage + 1);
  };

  const handleRefresh = () => {
    setRefresh(true);
  };

  const handleCase = async (data: any) => {
    try {
      const autoAssign = componentsData?.carePlan?.autoAssign;
      const payload = { ...data, ...(autoAssign && { assignedTo: authUser?.email }) };
      await CaseService.create(payload);
      handleRefresh();
      enqueueSnackbar('Case Created Succesfully', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar('There was an error trying to create case. Please try again', {
        variant: 'error',
      });
    }
  };

  const data = caseData?.data
    ? caseData.data.map((r: any) => workflowInstanceMap(r, PATH_DASHBOARD.crs.case))
    : [];

  const actionButtons = disableActions ? (
    <Button
      variant="contained"
      size="small"
      sx={{ height: '36px' }}
      startIcon={<Add />}
      onClick={() => setRefresh(true)}
    >
      Refresh
    </Button>
  ) : (
    <Button
      variant="contained"
      size="small"
      sx={{ height: '36px' }}
      startIcon={<Add />}
      onClick={() => setOpenAdd(true)}
    >
      {i18n('patients.details.cases.titleAddCases', 'crs')}
    </Button>
  );

  const permissions = checkAclValidation({ acls: [crsAcls.CRS.PATIENT.CASES.EDIT] });

  return (
    <>
      <SummaryTable
        title={title}
        columns={translateTableHead(TABLE_HEAD_WORKFLOW_INSTANCE_PATIENT, i18n, 'crs')}
        data={data}
        page={page}
        count={caseData ? caseData.meta.totalItems : 0}
        rowsPerPage={rowsPerPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={onChangeRowsPerPage}
        handleRefresh={handleRefresh}
        actions={permissions ? actionButtons : null}
        menuItems={(row) => [
          checkAclValidation({ acls: [crsAcls.CRS.PATIENT.CASES.EDIT] }) && (
            <MenuItem
              key="edit"
              onClick={() => {
                setRowEdit(row);
                setOpenEdit(true);
              }}
            >
              <Iconify icon="mdi:pencil" />
              Edit
            </MenuItem>
          ),

          checkAclValidation({ acls: [crsAcls.CRS.CASE.VIEW] }) && (
            <a
              key="detail"
              href={`${PATH_DASHBOARD.crs.case}/${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <MenuItem sx={{ color: 'black' }}>
                <OpenInNewIcon />
                Detail
              </MenuItem>
            </a>
          ),

          <PrintableCarePlan
            key={`print-${row.id}`}
            carePlanId={row.id}
            renderButton={(onClick) => (
              <MenuItem onClick={onClick}>
                <ArticleIcon />
                {i18n('case.details.generatePrintableCarePlan', 'crs')}
              </MenuItem>
            )}
          />,
        ]}
        width={260}
      />
      <CaseForm
        patient={patient}
        carePlan={null}
        open={add}
        onClose={() => setOpenAdd(false)}
        handleCase={handleCase}
        existData={data?.length > 0 ? true : false}
      />
      <Edit
        data={rowEdit}
        open={edit}
        onClose={() => setOpenEdit(false)}
        handleRefresh={handleRefresh}
        i18n={i18n}
      />
    </>
  );
}
